import {
  MonitoringDeviceRuleOverrideInput,
  MonitoringDeviceRuleOverrideInputRule,
  MonitoringDeviceRulesResponse,
  MonitoringRuleDeviceSummary,
  MonitoringRuleParamValue,
  MonitoringRuleProfile,
  MonitoringRuleProfileInput,
  MonitoringRuleProfileInputRule,
  MonitoringRuleCode,
} from './types';

export interface MonitoringDeviceRuleDraftRule {
  ruleCode: MonitoringRuleCode;
  isInherited: boolean;
  isEnabled: boolean;
  severity: MonitoringDeviceRulesResponse['resolvedRules'][number]['severity']['resolved'];
  params: Record<string, MonitoringRuleParamValue>;
}

export interface MonitoringDeviceRuleDraft {
  sensorId: string;
  rules: MonitoringDeviceRuleDraftRule[];
}

function sortRules<T extends { ruleCode: string }>(rules: T[]) {
  return [...rules].sort((left, right) => left.ruleCode.localeCompare(right.ruleCode));
}

export function cloneMonitoringRuleProfile(profile: MonitoringRuleProfile): MonitoringRuleProfile {
  return {
    ...profile,
    rules: profile.rules.map((rule) => ({
      ...rule,
      params: { ...rule.params },
      paramDefinitions: rule.paramDefinitions.map((definition) => ({ ...definition })),
    })),
  };
}

export function buildMonitoringRuleProfileInput(
  profile: MonitoringRuleProfile
): MonitoringRuleProfileInput {
  return {
    sensorType: profile.sensorType,
    rules: sortRules(
      profile.rules.map(
        (rule) =>
          ({
            ruleCode: rule.ruleCode,
            isEnabled: rule.isEnabled,
            severity: rule.severity,
            params: { ...rule.params },
          } as MonitoringRuleProfileInputRule)
      )
    ),
  };
}

export function hasMonitoringRuleProfileChanges(
  current: MonitoringRuleProfile,
  baseline: MonitoringRuleProfile
) {
  return (
    JSON.stringify(buildMonitoringRuleProfileInput(current)) !==
    JSON.stringify(buildMonitoringRuleProfileInput(baseline))
  );
}

export function buildMonitoringDeviceRuleDraft(
  response: MonitoringDeviceRulesResponse
): MonitoringDeviceRuleDraft {
  return {
    sensorId: response.sensorId,
    rules: response.resolvedRules.map((rule) => ({
      ruleCode: rule.ruleCode,
      isInherited: !rule.isOverridden,
      isEnabled: rule.isEnabled.resolved,
      severity: rule.severity.resolved,
      params: rule.params.reduce<Record<string, MonitoringRuleParamValue>>((result, param) => {
        result[param.key] = param.resolved;
        return result;
      }, {}),
    })),
  };
}

export function buildMonitoringDeviceRuleOverrideInput(
  draft: MonitoringDeviceRuleDraft,
  response: MonitoringDeviceRulesResponse
): MonitoringDeviceRuleOverrideInput {
  return {
    sensorId: draft.sensorId,
    rules: sortRules(
      draft.rules
        .filter((rule) => !rule.isInherited)
        .map((rule) => {
          const resolvedRule = response.resolvedRules.find(
            (candidate) => candidate.ruleCode === rule.ruleCode
          );

          return {
            ruleCode: rule.ruleCode,
            isEnabled: rule.isEnabled,
            severity: rule.severity,
            params: (resolvedRule?.params || []).reduce<
              Partial<Record<string, MonitoringRuleParamValue>>
            >((result, param) => {
              if (!param.readOnly && param.type !== 'source') {
                result[param.key] = rule.params[param.key];
              }
              return result;
            }, {}),
          } as MonitoringDeviceRuleOverrideInputRule;
        })
    ),
  };
}

function buildMonitoringDeviceRuleOverrideInputFromResponse(
  response: MonitoringDeviceRulesResponse
): MonitoringDeviceRuleOverrideInput {
  const rules = Object.entries(response.override?.rules || {})
    .map(([ruleCode, override]) => ({
      ruleCode: ruleCode as MonitoringRuleCode,
      ...(typeof override?.isEnabled === 'boolean' ? { isEnabled: override.isEnabled } : {}),
      ...(override?.severity ? { severity: override.severity } : {}),
      ...(override?.params ? { params: { ...override.params } } : {}),
    }))
    .filter(
      (rule) =>
        typeof rule.isEnabled === 'boolean' ||
        Boolean(rule.severity) ||
        Object.keys(rule.params || {}).length > 0
    );

  return {
    sensorId: response.sensorId,
    rules: sortRules(rules),
  };
}

export function hasMonitoringDeviceRuleChanges(
  draft: MonitoringDeviceRuleDraft,
  response: MonitoringDeviceRulesResponse
) {
  return (
    JSON.stringify(buildMonitoringDeviceRuleOverrideInput(draft, response)) !==
    JSON.stringify(buildMonitoringDeviceRuleOverrideInputFromResponse(response))
  );
}

export function filterMonitoringRuleDeviceSummaries(
  devices: MonitoringRuleDeviceSummary[],
  search: string
) {
  const normalized = search.trim().toLowerCase();
  if (!normalized) {
    return devices;
  }

  return devices.filter((device) =>
    [device.orgTitle, device.sensorTitle, device.deviceId, device.sensorType, device.deviceFamily]
      .filter(Boolean)
      .some((value) => value.toLowerCase().includes(normalized))
  );
}
